import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { ProspectsPanel } from "./ProspectsPanel";
import { ReferralAdminPage } from "./ReferralAdminPage";
import { FilterDisclosure } from "./FilterDisclosure";
import {
  ApiError,
  awardLaunchPack,
  startUseAs,
  getAdminAcquisition,
  getAdminAudit,
  getAdminCampaignCompanies,
  getAdminCampaignFeedback,
  getAdminCampaigns,
  getAdminHealth,
  getAdminSeoPerformance,
  getAdminPublicNetwork,
  getFailedNotificationDeliveries,
  getAdminInspection,
  getAdminOverview,
  getAdminSettings,
  getAdminUsers,
  getCredentialIntegrations,
  getInsuranceCatalogue,
  getStorageSettings,
  getTrackingProviders,
  queueFileMigration,
  saveAdminSetting,
  setAdminPublicNetworkState,
  saveCredentialIntegration,
  saveInsuranceCatalogue,
  saveStorageSettings,
  saveTrackingProvider,
  sendAdminCampaign,
  saveAdminCampaign,
  testStorageSettings,
  updateAdminCompany,
  updateAdminUser,
  type AdminAcquisition,
  type AdminCampaign,
  type AdminCampaignCompany,
  type AdminCampaignInput,
  type AdminCampaignFeedback,
  type AdminHealth,
  type SeoPerformance,
  type SeoRangeDays,
  type AdminPublicNetwork,
  type AdminInspection,
  type AdminInspectionKind,
  type AdminOverview,
  type AdminUser,
  type AuditFilters,
  type AuditResult,
  type CredentialIntegrationInput,
  type CredentialIntegrationKey,
  type CredentialIntegrationSummary,
  type InsuranceCatalogueItem,
  type StorageSettings,
  type StorageSettingsInput,
  type TrackingProviderSummary,
} from "./api";
import { useAuth } from "./auth";
import type { TrackingProviderKey } from "./api";

function campaignGoalLabel(goal: string) {
  return goal === "full_operation"
    ? "manage my fleet, loads and subcontractors"
    : goal.replaceAll("_", " ");
}

export type AdminSection =
  | "dashboard"
  | "acquisition"
  | "seo"
  | "prospects"
  | "campaigns"
  | "referrals"
  | "companies"
  | "users"
  | "operations"
  | "configuration"
  | "public-network"
  | "health"
  | "errors"
  | "integrations"
  | "audit";

export function AdminPage({
  section = "dashboard",
}: {
  section?: AdminSection;
}) {
  const auth = useAuth();
  const canEditIntegrations = auth.session?.platformRole === "owner";
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (
      section === "integrations" ||
      section === "audit" ||
      section === "seo" ||
      section === "acquisition" ||
      section === "prospects" ||
      section === "campaigns" ||
      section === "referrals"
    )
      return;
    void getAdminOverview()
      .then(setOverview)
      .catch((caught) =>
        setError(
          caught instanceof ApiError
            ? caught.message
            : "Unable to load platform administration.",
        ),
      );
  }, [section]);
  const labels: Record<AdminSection, [string, string]> = {
    dashboard: ["Dashboard", "Platform activity and health at a glance."],
    seo: [
      "SEO rankings",
      "Search visibility, traffic and position movement from Google Search Console.",
    ],
    acquisition: [
      "Acquisition",
      "See which channels turn into registered, group-owning and paying companies.",
    ],
    prospects: [
      "Prospects",
      "Track high-value network prospects and personalised outreach.",
    ],
    campaigns: [
      "Campaigns",
      "Send controlled company campaigns, track eligibility and review feedback.",
    ],
    referrals: [
      "Referrals",
      "Manage partner links, customer rewards and commission rules.",
    ],
    companies: ["Companies", "Company accounts, access and billing status."],
    users: ["Users and roles", "User access and scoped platform roles."],
    operations: [
      "Platform records",
      "Inspect operational and financial records within your role.",
    ],
    configuration: [
      "Platform defaults",
      "Insurance, notifications and storage migration.",
    ],
    "public-network": [
      "Public Network",
      "Membership totals, launch availability and recent membership activity.",
    ],
    health: [
      "System health",
      "Current service state and problems requiring attention.",
    ],
    errors: [
      "Application errors",
      "Unexpected failures captured with their user, company and request context.",
    ],
    integrations: ["Integrations", "External services used by LoadSub."],
    audit: [
      "Audit trail",
      "Immutable platform activity, searchable across companies and users.",
    ],
  };
  const [title, description] = labels[section];
  return (
    <>
      <header className="team-header">
        <div>
          <p className="admin-kicker">Platform administration</p>
          <h1 className="h3 mb-1">{title}</h1>
          <p className="text-secondary mb-0">{description}</p>
        </div>
        <span className="admin-owner-badge">
          {canEditIntegrations ? "Platform owner" : "Platform administrator"}
        </span>
      </header>
      <AdminNavigation role={auth.session?.platformRole ?? null} />
      {error && <div className="alert alert-danger">{error}</div>}
      {section === "dashboard" && <Dashboard overview={overview} />}
      {section === "acquisition" && <AcquisitionPanel />}
      {section === "seo" && <SeoRankingsPanel />}
      {section === "prospects" && <ProspectsPanel />}
      {section === "campaigns" && (
        <CampaignsPanel canAward={auth.session?.platformRole === "owner"} />
      )}
      {section === "referrals" && <ReferralAdminPage />}
      {section === "companies" && (
        <Companies
          overview={overview}
          role={auth.session?.platformRole ?? null}
          canUseAs={Boolean(auth.session?.canUseAs)}
        />
      )}
      {section === "users" && (
        <UsersPanel role={auth.session?.platformRole ?? null} />
      )}
      {section === "operations" && (
        <InspectionPanel role={auth.session?.platformRole ?? null} />
      )}
      {section === "configuration" && (
        <ConfigurationPanel role={auth.session?.platformRole ?? null} />
      )}
      {section === "public-network" && <PublicNetworkAdmin />}
      {section === "health" && <HealthPanel />}
      {section === "errors" && <ErrorsPanel />}
      {section === "integrations" && (
        <IntegrationsPanel canEdit={canEditIntegrations} />
      )}
      {section === "audit" && <AuditTrail />}
    </>
  );
}

function AdminNavigation({ role }: { role: string | null }) {
  const owner = role === "owner",
    admin = role === "owner" || role === "admin";
  return (
    <nav className="admin-navigation" aria-label="Platform administration">
      <section>
        <strong>Overview</strong>
        <div>
          <NavLink end to="/admin">
            Dashboard
          </NavLink>
          <NavLink to="/admin/progression">Progression</NavLink>
          {owner && <><NavLink to="/admin/acquisition">Acquisition</NavLink><NavLink to="/admin/seo">SEO rankings</NavLink></>}
        </div>
      </section>
      <section>
        <strong>People</strong>
        <div>
          {admin && (
            <>
              <NavLink to="/admin/companies">Companies</NavLink>
              <NavLink to="/admin/users">Users</NavLink>
              <NavLink to="/admin/prospects">Prospects</NavLink>
              <NavLink to="/admin/campaigns">Campaigns</NavLink>
              <NavLink to="/admin/referrals">Referrals</NavLink>
            </>
          )}
        </div>
      </section>
      <section>
        <strong>Operations</strong>
        <div>
          <NavLink to="/admin/operations">Records</NavLink>
          {role !== "finance" && (
            <>
              <NavLink to="/admin/health">Health</NavLink>
              <NavLink to="/admin/errors">Errors</NavLink>
            </>
          )}
          <NavLink to="/admin/audit">Audit</NavLink>
        </div>
      </section>
      {admin && (
        <section>
          <strong>Configuration</strong>
          <div>
            <NavLink to="/admin/configuration">Defaults</NavLink>
            <NavLink to="/admin/integrations">Integrations</NavLink>
            {owner && <NavLink to="/admin/google-ads">Google Ads</NavLink>}
          </div>
        </section>
      )}
    </nav>
  );
}

function publicPaymentStatus(status:string){
  const labels:Record<string,string>={
    pending_payment:"Checkout started",
    checkout_incomplete:"Checkout incomplete",
    processing:"Payment processing",
    requires_action:"Payment action required",
    payment_failed:"Payment failed",
    active:"Active",
    past_due:"Past due",
    cancelling:"Cancelling",
    cancelled:"Cancelled",
    refunded:"Refunded",
    interested:"Interested",
    invited:"Invited",
    withdrawn:"Withdrawn"
  };
  return labels[status]??status.replaceAll("_"," ");
}

function PublicNetworkAdmin() {
  const [data, setData] = useState<AdminPublicNetwork | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const refresh = () =>
    getAdminPublicNetwork()
      .then(setData)
      .catch((caught) =>
        setError(
          caught instanceof Error
            ? caught.message
            : "Unable to load Public Network administration.",
        ),
      );
  useEffect(() => {
    void refresh();
  }, []);
  if (!data)
    return error ? (
      <div className="alert alert-danger">{error}</div>
    ) : (
      <Loading />
    );
  const current = data,
    total = (test: (row: AdminPublicNetwork["counts"][number]) => boolean) =>
      current.counts.filter(test).reduce((sum, row) => sum + row.count, 0),
    active = total((row) => ["active", "cancelling"].includes(row.status)),
    awaiting =
      total((row) => row.status === "active") -
      current.members.filter((row) => row.status === "active" && row.rewardedAt)
        .length;
  async function change() {
    const next = current.state === "open" ? "closed" : "open",
      verb = next === "open" ? "open joining" : "pause new memberships";
    if (
      !confirm(
        `Confirm: ${verb}? Existing paid memberships will keep their access.`,
      )
    )
      return;
    setBusy(true);
    setError("");
    try {
      await setAdminPublicNetworkState(next);
      await refresh();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Launch state could not be changed.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="admin-public-network">
      {error && <div className="alert alert-danger">{error}</div>}
      <section className="team-panel">
        <div className="admin-section-heading">
          <div>
            <h2 className="h5 mb-1">Joining is {current.state}</h2>
            <p className="text-secondary small mb-0">
              This controls new registrations and Stripe Checkout. Existing paid
              memberships and live work are not interrupted.
            </p>
          </div>
          <button
            className={`btn ${current.state === "open" ? "btn-outline-warning" : "btn-success"}`}
            disabled={busy}
            onClick={() => void change()}
          >
            {busy
              ? "Saving…"
              : current.state === "open"
                ? "Pause new memberships"
                : "Open new memberships"}
          </button>
        </div>
      </section>
      <section className="admin-stats">
        <Stat label="Active access" value={active} />
        <Stat label="Awaiting first result" value={Math.max(0, awaiting)} />
        <Stat
          label="Post Loads"
          value={total((row) => row.tier === "load_provider")}
        />
        <Stat
          label="Post & Bid"
          value={total((row) => row.tier === "full_network")}
        />
      </section>
      <section className="team-panel">
        <div className="admin-section-heading">
          <div>
            <h2 className="h5 mb-1">Memberships</h2>
            <p className="text-secondary small mb-0">
              Live and sandbox records remain visibly separated.
            </p>
          </div>
        </div>
        <div className="admin-table-wrap">
          <table className="table admin-table mb-0">
            <thead>
              <tr>
                <th>Company</th>
                <th>Environment</th>
                <th>Tier</th>
                <th>Status</th>
                <th>Joined</th>
                <th>First result</th>
                <th>Renewal</th>
                <th>Paid</th>
              </tr>
            </thead>
            <tbody>
              {current.members.map((item) => (
                <tr key={item.companyId}>
                  <td>
                    <strong>{item.companyName}</strong>
                  </td>
                  <td>{item.environment}</td>
                  <td>
                    {item.tier === "full_network" ? "Post & Bid" : "Post Loads"}
                  </td>
                  <td>
                    <span
                      className={`status-chip status-${item.status === "active" ? "active" : "grace"}`}
                    >
                      {publicPaymentStatus(item.status)}
                    </span>
                  </td>
                  <td>{formatDate(item.registeredAt)}</td>
                  <td>
                    {item.rewardedAt ? formatDate(item.rewardedAt) : "Waiting"}
                  </td>
                  <td>
                    {item.currentPeriodEnd
                      ? formatDate(item.currentPeriodEnd)
                      : item.cancelAtPeriodEnd
                        ? "Cancelling"
                        : "Dormant"}
                  </td>
                  <td>£{(item.amountPaidPence / 100).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {current.members.length === 0 && (
          <p className="text-secondary mb-0">
            No Public Network memberships yet.
          </p>
        )}
      </section>
      <section className="team-panel">
        <div className="admin-section-heading">
          <div>
            <h2 className="h5 mb-1">Stripe event log</h2>
            <p className="text-secondary small mb-0">
              Authoritative live and sandbox webhook delivery, processing and billing references.
            </p>
          </div>
        </div>
        <div className="admin-table-wrap">
          <table className="table admin-table mb-0">
            <thead><tr><th>Received</th><th>Environment</th><th>Company</th><th>Event</th><th>Status</th><th>Amount</th><th>Stripe references</th><th>Error</th></tr></thead>
            <tbody>
              {current.stripeEvents.map((event) => (
                <tr key={`${event.environment}:${event.eventId}`}>
                  <td>{new Date(event.createdAt).toLocaleString("en-GB")}</td>
                  <td>{event.livemode ? "Live" : event.environment}</td>
                  <td>{event.companyName ?? "Unmatched"}</td>
                  <td><code>{event.eventType}</code><small className="d-block text-secondary">{event.eventId} · attempt {event.attempts}</small></td>
                  <td><span className={`status-chip status-${event.status === "processed" ? "active" : "grace"}`}>{event.status}</span></td>
                  <td>{event.amountPence === null ? "—" : `${(event.currency ?? "gbp").toUpperCase()} ${(event.amountPence / 100).toFixed(2)}`}</td>
                  <td><small>{[event.customerId,event.subscriptionId,event.paymentIntentId,event.checkoutSessionId].filter(Boolean).join(" · ") || "—"}</small></td>
                  <td>{event.lastError ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {current.stripeEvents.length === 0 && <p className="text-secondary mb-0">No Stripe webhook events recorded yet.</p>}
      </section>
      <section className="team-panel">
        <div className="admin-section-heading">
          <div>
            <h2 className="h5 mb-1">Recent Public Network events</h2>
            <p className="text-secondary small mb-0">
              Full membership changes and launch-control changes remain in the
              immutable audit trail.
            </p>
          </div>
          <NavLink className="btn btn-sm btn-outline-light" to="/admin/audit">
            Open full audit
          </NavLink>
        </div>
        <div className="admin-event-list">
          {current.recentEvents.slice(0, 20).map((event) => (
            <article key={event.id}>
              <div>
                <strong>{labelEvent(event.eventType)}</strong>
                <small>
                  {event.companyName ?? "Platform"} ·{" "}
                  {event.actorEmail ?? "System"}
                </small>
              </div>
              <time>{new Date(event.occurredAt).toLocaleString("en-GB")}</time>
            </article>
          ))}
        </div>
        {current.recentEvents.length === 0 && (
          <p className="text-secondary mb-0">
            No Public Network events recorded yet.
          </p>
        )}
      </section>
    </div>
  );
}

function CampaignsPanel({ canAward }: { canAward: boolean }) {
  const [campaigns, setCampaigns] = useState<AdminCampaign[]>([]),
    [selected, setSelected] =
      useState<AdminCampaign["key"]>("stalled_onboarding"),
    [companies, setCompanies] = useState<AdminCampaignCompany[]>([]),
    [feedback, setFeedback] = useState<AdminCampaignFeedback[]>([]),
    [chosen, setChosen] = useState<string[]>([]),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState(""),
    [editing, setEditing] = useState(false),
    [editingKey, setEditingKey] = useState<string | null>(null),
    [saving, setSaving] = useState(false),
    [form, setForm] = useState<AdminCampaignInput>({
      name: "", description: "", cooldownDays: 30, active: false,
      audience: "all_verified", subject: "", heading: "", body: "",
      actionLabel: "Open LoadSub", actionUrl: "/dashboard",
      footer: "This message was sent to a registered LoadSub company owner.",
    });
  const refresh = () =>
    Promise.all([
      getAdminCampaigns().then(setCampaigns),
      getAdminCampaignCompanies(selected).then(setCompanies),
      getAdminCampaignFeedback().then(setFeedback),
    ]);
  useEffect(() => {
    void refresh();
  }, [selected]);
  const selectedCampaign = campaigns.find((x) => x.key === selected),
    eligible = companies.filter((x) => x.eligible),
    allSelected =
      eligible.length > 0 &&
      eligible.every((x) => chosen.includes(x.companyId));
  async function send() {
    setBusy(true);
    setMessage("");
    try {
      const result = await sendAdminCampaign(selected, chosen),
        sent = result.results.filter((x) => x.status === "sent").length;
      setMessage(`${sent} campaign email${sent === 1 ? "" : "s"} sent.`);
      setChosen([]);
      await refresh();
    } catch (e) {
      setMessage(
        e instanceof Error ? e.message : "Campaign could not be sent.",
      );
    } finally {
      setBusy(false);
    }
  }
  function editCampaign(campaign?: AdminCampaign) {
    setMessage("");
    setEditingKey(campaign?.key ?? null);
    setForm(campaign ? {
      name: campaign.name, description: campaign.description,
      cooldownDays: campaign.cooldownDays, active: campaign.active,
      audience: campaign.audience, subject: campaign.subject,
      heading: campaign.heading, body: campaign.body,
      actionLabel: campaign.actionLabel, actionUrl: campaign.actionUrl,
      footer: campaign.footer,
    } : {
      name: "", description: "", cooldownDays: 30, active: false,
      audience: "all_verified", subject: "", heading: "", body: "",
      actionLabel: "Open LoadSub", actionUrl: "/dashboard",
      footer: "This message was sent to a registered LoadSub company owner.",
    });
    setEditing(true);
  }
  async function saveCampaign(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const result = await saveAdminCampaign(form, editingKey ?? undefined);
      setSelected(result.key);
      setEditing(false);
      setCampaigns(await getAdminCampaigns());
      setMessage("Campaign saved. It has not been sent.");
    } catch (e) {
      setMessage(e instanceof Error ? e.message : "Campaign could not be saved.");
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="campaign-admin">
      <section className="team-panel">
        <div className="admin-section-heading">
          <div>
            <h2 className="h5 mb-1">Campaign builder</h2>
            <p className="text-secondary small mb-0">Create and edit campaign emails here. New campaigns start as drafts and cannot be sent until marked ready.</p>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button className="btn btn-outline-light" type="button" onClick={() => editCampaign(selectedCampaign)} disabled={!selectedCampaign}>Edit selected</button>
            <button className="btn btn-primary" type="button" onClick={() => editCampaign()}>New campaign</button>
          </div>
        </div>
        {editing && <form onSubmit={saveCampaign} className="mt-3">
          <div className="row g-3">
            <label className="col-md-6">Campaign name<input className="form-control mt-1" required maxLength={120} value={form.name} onChange={e => setForm({...form,name:e.target.value})}/></label>
            <label className="col-md-6">Admin description<input className="form-control mt-1" required maxLength={300} value={form.description} onChange={e => setForm({...form,description:e.target.value})}/></label>
            <label className="col-md-6">Email subject<input className="form-control mt-1" required maxLength={200} value={form.subject} onChange={e => setForm({...form,subject:e.target.value})}/></label>
            <label className="col-md-6">Email heading<input className="form-control mt-1" required maxLength={200} value={form.heading} onChange={e => setForm({...form,heading:e.target.value})}/></label>
            <label className="col-12">Email body<textarea className="form-control mt-1" required rows={10} maxLength={20000} value={form.body} onChange={e => setForm({...form,body:e.target.value})}/></label>
            <label className="col-md-4">Button label<input className="form-control mt-1" required maxLength={100} value={form.actionLabel} onChange={e => setForm({...form,actionLabel:e.target.value})}/></label>
            <label className="col-md-8">Button destination<input className="form-control mt-1" required maxLength={500} placeholder="/dashboard or https://…" value={form.actionUrl} onChange={e => setForm({...form,actionUrl:e.target.value})}/></label>
            <label className="col-12">Footer<input className="form-control mt-1" required maxLength={500} value={form.footer} onChange={e => setForm({...form,footer:e.target.value})}/></label>
            <label className="col-md-4">Audience<select className="form-select mt-1" value={form.audience} onChange={e => setForm({...form,audience:e.target.value as AdminCampaignInput["audience"]})}><option value="all_verified">All verified company owners</option><option value="incomplete_onboarding">Incomplete onboarding only</option></select></label>
            <label className="col-md-4">Do not resend for<input className="form-control mt-1" type="number" min={1} max={365} required value={form.cooldownDays} onChange={e => setForm({...form,cooldownDays:Number(e.target.value)})}/><small className="text-secondary">Days</small></label>
            <label className="col-md-4 d-flex align-items-center gap-2 pt-md-4"><input type="checkbox" checked={form.active} onChange={e => setForm({...form,active:e.target.checked})}/> Ready to send</label>
          </div>
          <details className="mt-3">
            <summary>Email preview</summary>
            <div className="team-panel mt-2">
              <small className="text-secondary">{form.subject || "Email subject"}</small>
              <h3 className="h5 mt-2">{form.heading || "Email heading"}</h3>
              <p style={{whiteSpace:"pre-wrap"}}>{form.body || "Email body"}</p>
              <span className="btn btn-primary">{form.actionLabel || "Button label"}</span>
              <p className="small text-secondary mt-3 mb-0">{form.footer}</p>
            </div>
          </details>
          <div className="d-flex gap-2 mt-3">
            <button className="btn btn-primary" disabled={saving}>{saving ? "Saving…" : "Save campaign"}</button>
            <button className="btn btn-outline-light" type="button" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </form>}
      </section>
      <section className="campaign-card-grid">
        {campaigns.map((c) => (
          <button
            type="button"
            className={`team-panel campaign-card ${selected === c.key ? "is-selected" : ""}`}
            key={c.key}
            onClick={() => {
              setSelected(c.key);
              setChosen([]);
            }}
          >
            <strong>{c.name}</strong>
            <span>{c.description}</span>
            <small>
              {c.active ? "Ready" : "Draft"} · {c.eligible} eligible · {c.sent} sent · {c.feedback} feedback
            </small>
          </button>
        ))}
      </section>
      <section className="team-panel campaign-companies-panel">
        <div className="admin-section-heading">
          <div>
            <h2 className="h5">Eligible companies</h2>
            <p className="text-secondary small mb-0">
              The{" "}
              {campaigns.find((x) => x.key === selected)?.cooldownDays ?? 30}
              -day campaign window is enforced by the database. Ineligible
              companies cannot be selected.
            </p>
          </div>
          <button
            className="btn btn-primary campaign-send-button"
            disabled={busy || chosen.length === 0 || !selectedCampaign?.active}
            onClick={() => void send()}
          >
            {busy ? "Sending…" : `Send to ${chosen.length}`}
          </button>
        </div>
        {message && <div className="alert alert-info">{message}</div>}
        <div className="admin-table-wrap campaign-company-list">
          <table className="table admin-table campaign-company-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    aria-label="Select all eligible companies"
                    checked={allSelected}
                    onChange={() =>
                      setChosen(
                        allSelected ? [] : eligible.map((x) => x.companyId),
                      )
                    }
                  />
                </th>
                <th>Company</th>
                <th>Owner</th>
                <th>Signed up</th>
                <th>Last login</th>
                <th>Onboarding</th>
                <th>Last sent</th>
                <th>Eligibility</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((x) => (
                <tr key={x.companyId}>
                  <td className="campaign-select">
                    <input
                      type="checkbox"
                      aria-label={`Select ${x.tradingName}`}
                      disabled={!x.eligible}
                      checked={chosen.includes(x.companyId)}
                      onChange={() =>
                        setChosen((current) =>
                          current.includes(x.companyId)
                            ? current.filter((id) => id !== x.companyId)
                            : [...current, x.companyId],
                        )
                      }
                    />
                  </td>
                  <td className="campaign-company-name" data-label="Company">
                    <strong>{x.tradingName}</strong>
                  </td>
                  <td data-label="Owner">{x.email}</td>
                  <td data-label="Signed up">
                    {new Date(x.signupAt).toLocaleString("en-GB")}
                  </td>
                  <td data-label="Last login">
                    {x.lastLoginAt
                      ? new Date(x.lastLoginAt).toLocaleString("en-GB")
                      : "Never"}
                  </td>
                  <td data-label="Onboarding">
                    {x.completedSteps}/{x.totalSteps}
                    {x.goal ? ` · ${campaignGoalLabel(x.goal)}` : " · no goal"}
                  </td>
                  <td data-label="Last sent">
                    {x.lastSentAt
                      ? new Date(x.lastSentAt).toLocaleString("en-GB")
                      : "Never"}
                  </td>
                  <td className="campaign-eligibility" data-label="Eligibility">
                    {x.eligible ? (
                      <span className="status-chip status-active">
                        Eligible
                      </span>
                    ) : (
                      <small className="text-secondary">
                        {x.exclusionReason}
                      </small>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="campaign-company-cards">
          {companies.map((x) => (
            <article key={x.companyId}>
              <header>
                <label>
                  <input
                    type="checkbox"
                    aria-label={`Select ${x.tradingName}`}
                    disabled={!x.eligible}
                    checked={chosen.includes(x.companyId)}
                    onChange={() =>
                      setChosen((current) =>
                        current.includes(x.companyId)
                          ? current.filter((id) => id !== x.companyId)
                          : [...current, x.companyId],
                      )
                    }
                  />
                  <strong>{x.tradingName}</strong>
                </label>
                {x.eligible ? (
                  <span className="status-chip status-active">Eligible</span>
                ) : (
                  <span className="campaign-unavailable">Not eligible</span>
                )}
              </header>
              <div className="campaign-mobile-owner">
                <span>Owner</span>
                <strong>{x.email}</strong>
              </div>
              <dl>
                <div>
                  <dt>Signed up</dt>
                  <dd>{new Date(x.signupAt).toLocaleString("en-GB")}</dd>
                </div>
                <div>
                  <dt>Last login</dt>
                  <dd>
                    {x.lastLoginAt
                      ? new Date(x.lastLoginAt).toLocaleString("en-GB")
                      : "Never"}
                  </dd>
                </div>
                <div>
                  <dt>Onboarding</dt>
                  <dd>
                    {x.completedSteps}/{x.totalSteps}
                    {x.goal ? ` · ${campaignGoalLabel(x.goal)}` : " · no goal"}
                  </dd>
                </div>
                <div>
                  <dt>Last sent</dt>
                  <dd>
                    {x.lastSentAt
                      ? new Date(x.lastSentAt).toLocaleString("en-GB")
                      : "Never"}
                  </dd>
                </div>
              </dl>
              {!x.eligible && <p>{x.exclusionReason}</p>}
            </article>
          ))}
        </div>
      </section>
      <section className="team-panel">
        <h2 className="h5">Campaign feedback</h2>
        <p className="text-secondary small">
          Free-text responses are tied to the exact campaign send and company.
        </p>
        <div className="campaign-feedback-list">
          {feedback.length ? (
            feedback.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>{item.tradingName}</strong>
                  <small>
                    {item.userEmail} ·{" "}
                    {new Date(item.submittedAt).toLocaleString("en-GB")}
                  </small>
                </div>
                <p>{item.feedback}</p>
                <div>
                  {item.launchPackClaimedAt ? (
                    <span className="status-chip status-active">
                      Launch Pack claimed
                    </span>
                  ) : item.launchPackAwardedAt ? (
                    <button
                      className="btn btn-sm btn-outline-light"
                      disabled={!canAward}
                      onClick={() =>
                        void awardLaunchPack(item.id).then(() => refresh())
                      }
                    >
                      Resend award email
                    </button>
                  ) : (
                    <button
                      className="btn btn-sm btn-success"
                      disabled={!canAward}
                      onClick={() =>
                        void awardLaunchPack(item.id).then(() => refresh())
                      }
                    >
                      Give launch pack
                    </button>
                  )}
                </div>
              </article>
            ))
          ) : (
            <p className="text-secondary">No feedback received yet.</p>
          )}
        </div>
      </section>
    </div>
  );
}

function AcquisitionPanel() {
  const [data, setData] = useState<AdminAcquisition | null>(null),
    [error, setError] = useState("");
  useEffect(() => {
    void getAdminAcquisition()
      .then(setData)
      .catch((e) =>
        setError(
          e instanceof Error ? e.message : "Unable to load acquisition data.",
        ),
      );
  }, []);
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!data) return <Loading />;
  return (
    <section className="acquisition-dashboard">
      <div className="admin-stats">
        <Stat label="Registrations" value={data.totals.registrations} />
        <Stat label="Created a group" value={data.totals.groupCreators} />
        <Stat label="Paid or exempt" value={data.totals.paidCompanies} />
      </div>
      <div className="team-panel">
        <div className="admin-section-heading">
          <div>
            <h2 className="h5 mb-1">
              Registration outcomes by first-touch source
            </h2>
            <p className="text-secondary small mb-0">
              Attribution is fixed when the company registers. Historic
              registrations appear as unknown.
            </p>
          </div>
        </div>
        <div className="admin-table-wrap">
          <table className="table admin-table mb-0">
            <thead>
              <tr>
                <th>Source</th>
                <th>Registrations</th>
                <th>Group creators</th>
                <th>Paid/exempt</th>
                <th>Share</th>
              </tr>
            </thead>
            <tbody>
              {data.bySource.map((row) => (
                <tr key={row.source}>
                  <td>
                    <strong>{acquisitionLabel(row.source)}</strong>
                  </td>
                  <td>{row.registrations}</td>
                  <td>{row.groupCreators}</td>
                  <td>{row.paidCompanies}</td>
                  <td>
                    <div className="acquisition-bar">
                      <span
                        style={{
                          width: `${Math.round((row.registrations / Math.max(1, data.totals.registrations)) * 100)}%`,
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="acquisition-grid">
        <Breakdown
          title="Campaigns"
          rows={data.byCampaign.map((x) => [x.campaign, x.registrations])}
        />
        <Breakdown
          title="Medium"
          rows={data.byMedium.map((x) => [x.medium, x.registrations])}
        />
        <Breakdown
          title="Business type"
          rows={data.byBusinessType.map((x) => [
            x.businessType,
            x.registrations,
          ])}
        />
      </div>
      <div className="team-panel">
        <h2 className="h5">Last 30 days</h2>
        <div
          className="acquisition-trend"
          aria-label="Registrations in the last 30 days"
        >
          {data.trend.map((day) => (
            <div key={day.date} title={`${day.date}: ${day.registrations}`}>
              <span
                style={{
                  height: `${Math.max(day.registrations ? 8 : 2, (day.registrations / Math.max(1, ...data.trend.map((x) => x.registrations))) * 100)}%`,
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
function Breakdown({
  title,
  rows,
}: {
  title: string;
  rows: Array<[string, number]>;
}) {
  return (
    <section className="team-panel">
      <h2 className="h6">{title}</h2>
      <dl className="acquisition-list">
        {rows.map(([name, count]) => (
          <div key={name}>
            <dt>{acquisitionLabel(name)}</dt>
            <dd>{count}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
function acquisitionLabel(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (x) => x.toUpperCase());
}

function Dashboard({ overview }: { overview: AdminOverview | null }) {
  if (!overview) return <Loading />;
  return (
    <section className="admin-stats">
      <Stat label="Companies" value={overview.totals.companies} />
      <Stat label="Active users" value={overview.totals.users} />
      <Stat label="Private groups" value={overview.totals.groups} />
      <Stat label="Active loads" value={overview.totals.activeLoads} />
    </section>
  );
}

function Companies({
  overview,
  role,
  canUseAs,
}: {
  overview: AdminOverview | null;
  role: string | null;
  canUseAs: boolean;
}) {
  const [busy, setBusy] = useState("");
  if (!overview) return <Loading />;
  const editable = role === "owner" || role === "admin";
  async function status(
    id: string,
    value: AdminOverview["companies"][number]["status"],
  ) {
    setBusy(id);
    try {
      await updateAdminCompany(id, { status: value });
      location.reload();
    } finally {
      setBusy("");
    }
  }
  async function exemption(company: AdminOverview["companies"][number]) {
    const enabled = !company.billingExempt,
      reason = enabled
        ? window.prompt("Reason for billing exemption")?.trim()
        : null;
    if (enabled && !reason) return;
    setBusy(company.id);
    try {
      await updateAdminCompany(company.id, {
        billingExempt: enabled,
        billingExemptReason: reason,
      });
      location.reload();
    } finally {
      setBusy("");
    }
  }
  async function useAs(company: AdminOverview["companies"][number]) {
    if (
      !window.confirm(
        `Use LoadSub as ${company.tradingName}? Your actions will be logged as LoadSub Team on behalf of ${company.tradingName}.`,
      )
    )
      return;
    setBusy(company.id);
    try {
      await startUseAs(company.id);
      location.assign("/dashboard");
    } catch {
      window.alert("Use As was denied.");
    } finally {
      setBusy("");
    }
  }
  return (
    <section className="team-panel">
      <div className="admin-section-heading">
        <div>
          <h2 className="h5 mb-1">Companies</h2>
          <p className="text-secondary small mb-0">
            Suspension immediately revokes active sessions. Exemptions and
            LoadSub-assisted setup are audited.
          </p>
        </div>
      </div>
      <div className="admin-table-wrap">
        <table className="table admin-table mb-0">
          <thead>
            <tr>
              <th>Company</th>
              <th>Status</th>
              <th>Billing</th>
              <th>Users</th>
              <th>Trial</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {overview.companies.map((company) => (
              <tr key={company.id}>
                <td>
                  <strong>{company.tradingName}</strong>
                  <small className="d-block text-secondary">
                    {formatDate(company.createdAt)}
                  </small>
                </td>
                <td>
                  {editable ? (
                    <select
                      className="form-select form-select-sm"
                      disabled={busy === company.id}
                      value={company.status}
                      onChange={(e) =>
                        void status(
                          company.id,
                          e.target.value as typeof company.status,
                        )
                      }
                    >
                      {[
                        "trial",
                        "active",
                        "grace",
                        "read_only",
                        "suspended",
                        "closed",
                      ].map((x) => (
                        <option key={x}>{x}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`status-chip status-${company.status}`}>
                      {company.status.replace("_", " ")}
                    </span>
                  )}
                </td>
                <td>
                  {company.billingExempt ? (
                    <span title={company.billingExemptReason ?? undefined}>
                      Exempt
                    </span>
                  ) : (
                    "Standard"
                  )}
                </td>
                <td>{company.activeUsers}</td>
                <td>
                  {company.billingExempt
                    ? "—"
                    : formatDate(company.trialEndsAt)}
                </td>
                <td>
                  <div className="d-flex flex-wrap gap-2">
                    {role === "owner" && (
                      <button
                        className="btn btn-sm btn-outline-light"
                        disabled={busy === company.id}
                        onClick={() => void exemption(company)}
                      >
                        {company.billingExempt
                          ? "Remove exemption"
                          : "Exempt billing"}
                      </button>
                    )}
                    {canUseAs && (
                      <button
                        className="btn btn-sm btn-primary"
                        disabled={
                          busy === company.id ||
                          ["suspended", "closed"].includes(company.status)
                        }
                        onClick={() => void useAs(company)}
                      >
                        {busy === company.id ? "Opening…" : "Use As"}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AuditTrail() {
  const empty: AuditFilters = {};
  const [form, setForm] = useState<AuditFilters>(empty);
  const [applied, setApplied] = useState<AuditFilters>(empty);
  const [result, setResult] = useState<AuditResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function load(filters: AuditFilters) {
    setBusy(true);
    setError("");
    try {
      setResult(await getAdminAudit(normalizeDates(filters)));
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Unable to load audit events.",
      );
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    void load({});
  }, []);
  function apply(event: FormEvent) {
    event.preventDefault();
    const next = { ...form, page: 1 };
    setApplied(next);
    void load(next);
  }
  function page(value: number) {
    const next = { ...applied, page: value };
    setApplied(next);
    void load(next);
  }
  function clear() {
    setForm({});
    setApplied({});
    void load({});
  }
  const activeCount = Object.entries(applied).filter(
    ([key, value]) => key !== "page" && Boolean(value),
  ).length;
  return (
    <>
      <FilterDisclosure
        activeCount={activeCount}
        summary={
          result
            ? `${result.total.toLocaleString("en-GB")} event${result.total === 1 ? "" : "s"}`
            : "Loading…"
        }
      >
        <form className="audit-filters" onSubmit={apply}>
          <Filter label="Category">
            <select
              className="form-select"
              value={form.category ?? ""}
              onChange={(e) =>
                setForm({ ...form, category: e.target.value || undefined })
              }
            >
              <option value="">All categories</option>
              {result?.options.categories.map((value) => (
                <option key={value} value={value}>
                  {labelEvent(value)}
                </option>
              ))}
            </select>
          </Filter>
          <Filter label="Event">
            <select
              className="form-select"
              value={form.eventType ?? ""}
              onChange={(e) =>
                setForm({ ...form, eventType: e.target.value || undefined })
              }
            >
              <option value="">All event types</option>
              {result?.options.eventTypes.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </Filter>
          <Filter label="Company">
            <select
              className="form-select"
              value={form.companyId ?? ""}
              onChange={(e) =>
                setForm({ ...form, companyId: e.target.value || undefined })
              }
            >
              <option value="">All companies</option>
              {result?.options.companies.map((value) => (
                <option key={value.id} value={value.id}>
                  {value.name}
                </option>
              ))}
            </select>
          </Filter>
          <Filter label="User">
            <select
              className="form-select"
              value={form.userId ?? ""}
              onChange={(e) =>
                setForm({ ...form, userId: e.target.value || undefined })
              }
            >
              <option value="">All users</option>
              {result?.options.users.map((value) => (
                <option key={value.id} value={value.id}>
                  {value.email}
                </option>
              ))}
            </select>
          </Filter>
          <Filter label="Entity type">
            <select
              className="form-select"
              value={form.entityType ?? ""}
              onChange={(e) =>
                setForm({ ...form, entityType: e.target.value || undefined })
              }
            >
              <option value="">All entities</option>
              {result?.options.entityTypes.map((value) => (
                <option key={value}>{value}</option>
              ))}
            </select>
          </Filter>
          <Filter label="Entity / load ID">
            <input
              className="form-control"
              placeholder="UUID"
              value={form.entityId ?? ""}
              onChange={(e) =>
                setForm({ ...form, entityId: e.target.value || undefined })
              }
            />
          </Filter>
          <Filter label="From">
            <input
              className="form-control"
              type="date"
              value={form.from ?? ""}
              onChange={(e) =>
                setForm({ ...form, from: e.target.value || undefined })
              }
            />
          </Filter>
          <Filter label="To">
            <input
              className="form-control"
              type="date"
              value={form.to ?? ""}
              onChange={(e) =>
                setForm({ ...form, to: e.target.value || undefined })
              }
            />
          </Filter>
          <div className="audit-filter-actions">
            <button className="btn btn-primary" disabled={busy}>
              {busy ? "Filtering…" : "Apply filters"}
            </button>
            <button
              className="btn btn-outline-light"
              type="button"
              onClick={clear}
            >
              Clear
            </button>
          </div>
        </form>
      </FilterDisclosure>
      {error && <div className="alert alert-danger mt-3">{error}</div>}
      <section className="team-panel mt-3">
        <div className="audit-heading">
          <div>
            <h2 className="h5 mb-1">Events</h2>
            <p className="text-secondary small mb-0">
              {result
                ? `${result.total.toLocaleString("en-GB")} events · 50 per page`
                : "Loading…"}
            </p>
          </div>
          {result && (
            <span>
              Page {result.page} of {result.pages}
            </span>
          )}
        </div>
        <div className="audit-list">
          {result?.events.map((event) => (
            <details className="audit-event" key={event.id}>
              <summary>
                <span className="audit-event-type">
                  {labelEvent(event.eventType)}
                </span>
                <span>{event.companyName ?? "Platform"}</span>
                <span>{event.actorEmail ?? "System"}</span>
                <time>
                  {new Date(event.occurredAt).toLocaleString("en-GB")}
                </time>
              </summary>
              <dl>
                <Detail label="Event" value={event.eventType} />
                <Detail
                  label="Entity"
                  value={`${event.entityType}${event.entityId ? ` · ${event.entityId}` : ""}`}
                />
                <Detail
                  label="Company"
                  value={event.companyName ?? event.companyId ?? "Platform"}
                />
                <Detail
                  label="User"
                  value={event.actorEmail ?? event.actorUserId ?? "System"}
                />
                <Detail
                  label="Request ID"
                  value={event.requestId ?? "Not recorded"}
                />
              </dl>
              {Object.keys(event.metadata).length > 0 && (
                <pre>{JSON.stringify(event.metadata, null, 2)}</pre>
              )}
            </details>
          ))}
        </div>
        {result && result.events.length === 0 && (
          <p className="text-secondary mb-0">No events match these filters.</p>
        )}
        {result && result.pages > 1 && (
          <div className="audit-pagination">
            <button
              className="btn btn-sm btn-outline-light"
              disabled={busy || result.page <= 1}
              onClick={() => page(result.page - 1)}
            >
              Previous
            </button>
            <span>
              {result.page} / {result.pages}
            </span>
            <button
              className="btn btn-sm btn-outline-light"
              disabled={busy || result.page >= result.pages}
              onClick={() => page(result.page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>
    </>
  );
}

function Filter({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label>
      <span>{label}</span>
      {children}
    </label>
  );
}
function normalizeDates(filters: AuditFilters): AuditFilters {
  const next = { ...filters };
  if (next.from) next.from = new Date(`${next.from}T00:00:00`).toISOString();
  if (next.to) {
    const end = new Date(`${next.to}T00:00:00`);
    end.setDate(end.getDate() + 1);
    next.to = end.toISOString();
  }
  return next;
}
function labelEvent(value: string) {
  return value
    .replaceAll("_", " ")
    .replaceAll(".", " · ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

type FieldDefinition = {
  name: string;
  label: string;
  secret?: boolean;
  placeholder?: string;
};
const integrationDefinitions: Record<
  CredentialIntegrationKey,
  {
    title: string;
    description: string;
    providerPlaceholder: string;
    fields: FieldDefinition[];
  }
> = {
  stripe: {
    title: "Stripe",
    description:
      "Live and sandbox credentials are stored separately. Each environment keeps its own products and recurring prices.",
    providerPlaceholder: "stripe",
    fields: [
      { name: "livePublishableKey", label: "Live publishable key" },
      { name: "sandboxPublishableKey", label: "Sandbox publishable key" },
      { name: "liveSecretKey", label: "Live secret key", secret: true },
      { name: "sandboxSecretKey", label: "Sandbox secret key", secret: true },
    ],
  },
  email: {
    title: "Transactional email",
    description:
      "Provider-neutral delivery configuration and delivery webhooks.",
    providerPlaceholder: "resend, postmark, sendgrid, smtp…",
    fields: [
      {
        name: "environment",
        label: "Environment",
        placeholder: "staging or production",
      },
      { name: "stagingRecipient", label: "Staging email recipient" },
      { name: "fromName", label: "From name" },
      { name: "fromAddress", label: "From address" },
      { name: "replyToAddress", label: "Reply-to address" },
      { name: "verifiedDomain", label: "Verified domain" },
      { name: "apiBaseUrl", label: "API base URL" },
      { name: "smtpHost", label: "SMTP host" },
      { name: "smtpPort", label: "SMTP port" },
      { name: "apiKey", label: "API key", secret: true },
      { name: "smtpUsername", label: "SMTP username", secret: true },
      { name: "smtpPassword", label: "SMTP password", secret: true },
      {
        name: "deliveryWebhookSecret",
        label: "Delivery webhook secret",
        secret: true,
      },
    ],
  },
  geocoding: {
    title: "UK postcodes",
    description: "Built-in UK postcode lookup using Postcodes.io. No API key or separate subscription is required.",
    providerPlaceholder: "postcodes.io, mapbox, google…",
    fields: [
      { name: "baseUrl", label: "API base URL" },
      { name: "country", label: "Default country", placeholder: "GB" },
      { name: "apiKey", label: "API key", secret: true },
    ],
  },
  web_push: {
    title: "Web Push",
    description: "Built-in notification identity, generated and encrypted automatically by LoadSub.",
    providerPlaceholder: "vapid",
    fields: [
      {
        name: "subject",
        label: "Contact subject",
        placeholder: "mailto:support@loadsub.com",
      },
      { name: "publicKey", label: "VAPID public key" },
      { name: "privateKey", label: "VAPID private key", secret: true },
    ],
  },
  maps: {
    title: "Position maps",
    description: "Built-in OpenStreetMap display for live vehicle positions. No API key or separate subscription is required; route and ETA calculations remain separate.",
    providerPlaceholder: "openstreetmap, mapbox, google…",
    fields: [
      { name: "tileUrl", label: "Tile URL template" },
      { name: "styleUrl", label: "Style URL" },
      { name: "apiKey", label: "API key", secret: true },
    ],
  },
  monitoring: {
    title: "Application monitoring",
    description:
      "Production error reporting without exposing credentials to clients.",
    providerPlaceholder: "sentry",
    fields: [
      { name: "environment", label: "Environment", placeholder: "production" },
      { name: "dsn", label: "DSN", secret: true },
    ],
  },
  google_ads: {
    title: "Google Ads",
    description: "Owner-only Google Ads campaign controls using LoadSub’s existing Google service account. Every live write is validated, previewed and audited.",
    providerPlaceholder: "Google Ads",
    fields: [
      { name: "customerId", label: "Google Ads customer ID", placeholder: "6699856299" },
      { name: "loginCustomerId", label: "Manager account ID (optional)" },
      { name: "apiVersion", label: "Google Ads API version", placeholder: "v25" },
      { name: "developerToken", label: "Developer token (optional)", secret: true },
    ],
  },
  meta_social: {
    title: "Facebook & Instagram publishing",
    description:
      "Secure Meta Graph API configuration for approved LoadSub posts. Facebook posts share to Instagram by default.",
    providerPlaceholder: "meta",
    fields: [
      {
        name: "graphApiVersion",
        label: "Graph API version",
        placeholder: "vXX.X",
      },
      { name: "facebookPageId", label: "Facebook Page ID" },
      {
        name: "instagramBusinessAccountId",
        label: "Instagram business account ID",
      },
      {
        name: "defaultShareToInstagram",
        label: "Default share to Instagram",
        placeholder: "true",
      },
      { name: "pageAccessToken", label: "Page access token", secret: true },
    ],
  },
};

function IntegrationsPanel({ canEdit }: { canEdit: boolean }) {
  const [integrations, setIntegrations] = useState<
    CredentialIntegrationSummary[] | null
  >(null);
  const [error, setError] = useState("");
  useEffect(() => {
    void getCredentialIntegrations()
      .then(setIntegrations)
      .catch((caught) =>
        setError(
          caught instanceof ApiError
            ? caught.message
            : "Unable to load integration settings.",
        ),
      );
  }, []);
  function changed(value: CredentialIntegrationSummary) {
    setIntegrations(
      (current) =>
        current?.map((item) => (item.key === value.key ? value : item)) ?? [
          value,
        ],
    );
  }
  return (
    <div className="integration-stack">
      {!canEdit && (
        <div className="alert alert-info">
          Platform administrators can inspect configuration and masked
          credential status. Only the platform owner can change credentials.
        </div>
      )}
      {error && <div className="alert alert-danger">{error}</div>}
      <FleetTrackingAdminPanel canEdit={canEdit} />
      <AnalyticsPanel canEdit={canEdit} />
      <StoragePanel canEdit={canEdit} />
      {integrations ? (
        integrations.map((item) => (
          <CredentialIntegrationCard
            key={item.key}
            summary={item}
            canEdit={canEdit}
            changed={changed}
          />
        ))
      ) : (
        <Loading />
      )}
    </div>
  );
}

type TrackingPlatformField = {
  name: string;
  label: string;
  secret?: boolean;
  placeholder?: string;
};
const trackingPlatformDefinitions: Record<
  TrackingProviderKey,
  { description: string; fields: TrackingPlatformField[] }
> = {
  loadsub_device: {
    description:
      "Uses GPS from the driver's LoadSub PWA during an active assigned load. No external tracking system or platform credentials are required.",
    fields: [],
  },
  samsara: {
    description:
      "Customers authenticate with their own Samsara API token. LoadSub only needs the API base URL.",
    fields: [{ name: "apiBaseUrl", label: "API base URL" }],
  },
  geotab: {
    description:
      "Customers authenticate with their own MyGeotab database, username and password. LoadSub only needs the API base URL.",
    fields: [{ name: "apiBaseUrl", label: "API base URL" }],
  },
  webfleet: {
    description:
      "WEBFLEET.connect requires a LoadSub API key as well as each customer's enabled Webfleet account.",
    fields: [
      { name: "apiBaseUrl", label: "API base URL" },
      {
        name: "apiKey",
        label: "LoadSub WEBFLEET.connect API key",
        secret: true,
      },
    ],
  },
  quartix: {
    description:
      "Customers supply their own Quartix API token after Quartix enables access. Configure the API endpoint and response mapping here before release.",
    fields: restTrackingFields(),
  },
  microlise: {
    description:
      "Customers supply credentials provisioned by Microlise. Configure the API endpoint and response mapping here after Microlise confirms access.",
    fields: restTrackingFields(),
  },
};
function restTrackingFields(): TrackingPlatformField[] {
  return [
    { name: "apiBaseUrl", label: "API base URL" },
    { name: "vehiclesPath", label: "Vehicles path", placeholder: "/vehicles" },
    {
      name: "vehiclesArrayPath",
      label: "Vehicles array path",
      placeholder: "data",
    },
    { name: "vehicleIdField", label: "Vehicle ID field", placeholder: "id" },
    {
      name: "vehicleLabelField",
      label: "Vehicle label field",
      placeholder: "name",
    },
    {
      name: "registrationField",
      label: "Registration field",
      placeholder: "registration",
    },
    {
      name: "positionsPath",
      label: "Positions path",
      placeholder: "/positions",
    },
    {
      name: "positionsArrayPath",
      label: "Positions array path",
      placeholder: "data",
    },
    {
      name: "positionVehicleIdField",
      label: "Position vehicle ID field",
      placeholder: "vehicleId",
    },
    { name: "latitudeField", label: "Latitude field", placeholder: "latitude" },
    {
      name: "longitudeField",
      label: "Longitude field",
      placeholder: "longitude",
    },
    {
      name: "recordedAtField",
      label: "Recorded-at field",
      placeholder: "recordedAt",
    },
    { name: "speedField", label: "Speed field", placeholder: "speedKph" },
    { name: "headingField", label: "Heading field", placeholder: "heading" },
  ];
}

function FleetTrackingAdminPanel({ canEdit }: { canEdit: boolean }) {
  const [providers, setProviders] = useState<TrackingProviderSummary[] | null>(
      null,
    ),
    [editing, setEditing] = useState<string>(),
    [forms, setForms] = useState<
      Record<
        string,
        {
          enabled: boolean;
          configuration: Record<string, string>;
          secrets: Record<string, string>;
        }
      >
    >({}),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  useEffect(() => {
    void getTrackingProviders()
      .then((values) => {
        setProviders(values);
        setForms(
          Object.fromEntries(
            values.map((x) => [
              x.key,
              {
                enabled: x.enabled,
                configuration: x.configuration,
                secrets: {},
              },
            ]),
          ),
        );
      })
      .catch((e) =>
        setError(
          e instanceof ApiError
            ? e.message
            : "Unable to load fleet tracking providers.",
        ),
      );
  }, []);
  async function save(provider: TrackingProviderSummary) {
    const form = forms[provider.key]!;
    setBusy(true);
    setError("");
    try {
      const saved = await saveTrackingProvider(provider.key, {
        enabled: form.enabled,
        authType: provider.authType,
        configuration: form.configuration,
        ...(Object.values(form.secrets).some(Boolean)
          ? { secrets: form.secrets }
          : {}),
      });
      setProviders(
        (current) =>
          current?.map((x) => (x.key === saved.key ? saved : x)) ?? [saved],
      );
      setEditing(undefined);
    } catch (e) {
      setError(
        e instanceof ApiError ? e.message : "Unable to save tracking provider.",
      );
    } finally {
      setBusy(false);
    }
  }
  function change(
    key: string,
    part: Partial<{
      enabled: boolean;
      configuration: Record<string, string>;
      secrets: Record<string, string>;
    }>,
  ) {
    setForms((current) => ({
      ...current,
      [key]: { ...current[key]!, ...part },
    }));
  }
  return (
    <section className="team-panel integration-card">
      <div className="admin-section-heading">
        <div>
          <p className="admin-kicker">Paid add-on</p>
          <h2 className="h5 mb-1">Fleet Tracking</h2>
          <p className="text-secondary small mb-0">
            Provider availability and the platform settings each integration
            actually uses. Customer credentials stay inside each company’s own
            integration panel.
          </p>
        </div>
      </div>
      {error && <div className="alert alert-danger mt-3">{error}</div>}
      <div className="integration-stack mt-3">
        {providers?.map((provider) => {
          const form = forms[provider.key],
            definition = trackingPlatformDefinitions[provider.key],
            ordinary = definition.fields.filter((field) => !field.secret),
            secret = definition.fields.filter((field) => field.secret);
          return (
            <article className="tracking-provider-card" key={provider.key}>
              <div>
                <strong>{provider.displayName}</strong>
                <small className="tracking-provider-description">
                  {definition.description}
                </small>
              </div>
              {editing === provider.key && form ? (
                <div className="settings-grid mt-3">
                  <label className="check-setting">
                    <input
                      type="checkbox"
                      checked={form.enabled}
                      onChange={(e) =>
                        change(provider.key, { enabled: e.target.checked })
                      }
                    />
                    <span>Offer this provider to customers</span>
                  </label>
                  {ordinary.map((field) => (
                    <Field key={field.name} label={field.label}>
                      <input
                        className="form-control"
                        placeholder={field.placeholder}
                        value={form.configuration[field.name] ?? ""}
                        onChange={(e) =>
                          change(provider.key, {
                            configuration: {
                              ...form.configuration,
                              [field.name]: e.target.value,
                            },
                          })
                        }
                      />
                    </Field>
                  ))}
                  {secret.map((field) => (
                    <Field
                      key={field.name}
                      label={`${field.label}${provider.secretHints[field.name] ? ` · ${provider.secretHints[field.name]}` : ""}`}
                    >
                      <input
                        className="form-control"
                        type="password"
                        autoComplete="new-password"
                        placeholder={
                          provider.secretHints[field.name]
                            ? "Leave blank to retain"
                            : ""
                        }
                        value={form.secrets[field.name] ?? ""}
                        onChange={(e) =>
                          change(provider.key, {
                            secrets: {
                              ...form.secrets,
                              [field.name]: e.target.value,
                            },
                          })
                        }
                      />
                    </Field>
                  ))}
                  <div className="storage-actions">
                    <button
                      className="btn btn-primary"
                      disabled={busy}
                      onClick={() => void save(provider)}
                    >
                      Save provider
                    </button>
                    <button
                      className="btn btn-outline-light"
                      onClick={() => setEditing(undefined)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="integration-actions">
                  <span
                    className={`status-chip ${provider.enabled ? "status-active" : "status-grace"}`}
                  >
                    {provider.enabled ? "Available now" : "Coming soon"}
                  </span>
                  <span
                    className={`status-chip ${provider.configured ? "status-active" : "status-grace"}`}
                  >
                    {provider.configured
                      ? "Platform ready"
                      : "Platform setup required"}
                  </span>
                  {canEdit && (
                    <button
                      className="btn btn-sm btn-outline-light"
                      onClick={() => setEditing(provider.key)}
                    >
                      Configure
                    </button>
                  )}
                </div>
              )}
            </article>
          );
        }) ?? <Loading />}
      </div>
    </section>
  );
}

function AnalyticsPanel({ canEdit }: { canEdit: boolean }) {
  const [measurementId, setMeasurementId] = useState("");
  const [savedId, setSavedId] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  useEffect(() => {
    void getAdminSettings()
      .then((settings) => {
        const value = settings.google_analytics;
        const id =
          typeof value === "object" &&
          value !== null &&
          typeof (value as { measurementId?: unknown }).measurementId ===
            "string"
            ? (value as { measurementId: string }).measurementId
            : "";
        setMeasurementId(id);
        setSavedId(id);
        setLoaded(true);
      })
      .catch((caught) =>
        setError(
          caught instanceof ApiError
            ? caught.message
            : "Unable to load Google Analytics settings.",
        ),
      );
  }, []);
  async function save(event: FormEvent) {
    event.preventDefault();
    const normalized = measurementId.trim().toUpperCase();
    if (normalized && !/^G-[A-Z0-9]{6,20}$/.test(normalized)) {
      setError("Enter a GA4 measurement ID in the format G-XXXXXXXXXX.");
      return;
    }
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await saveAdminSetting("google_analytics", { measurementId: normalized });
      setMeasurementId(normalized);
      setSavedId(normalized);
      setEditing(false);
      setNotice(
        normalized
          ? "Google Analytics measurement ID saved. New consenting visits will use it immediately."
          : "Google Analytics disabled. No new visits will be sent.",
      );
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Unable to save Google Analytics settings.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="team-panel integration-card">
      <div className="admin-section-heading">
        <div>
          <p className="admin-kicker">Traffic analytics</p>
          <h2 className="h5 mb-1">Google Analytics 4</h2>
          <p className="text-secondary small mb-0">
            Public pages and sign-in or registration only. Operational app
            activity is excluded.
          </p>
        </div>
        <span
          className={`status-chip ${measurementId ? "status-active" : "status-grace"}`}
        >
          {measurementId ? "Configured" : "Setup required"}
        </span>
      </div>
      {error && <div className="alert alert-danger py-2 mt-3">{error}</div>}
      {notice && <div className="alert alert-success py-2 mt-3">{notice}</div>}
      {!loaded ? (
        <Loading />
      ) : editing ? (
        <form className="storage-form mt-3" onSubmit={save}>
          <Field label="GA4 measurement ID">
            <input
              className="form-control"
              autoComplete="off"
              maxLength={22}
              placeholder="G-XXXXXXXXXX"
              value={measurementId}
              onChange={(event) =>
                setMeasurementId(event.target.value.toUpperCase())
              }
            />
          </Field>
          <p className="text-secondary small">
            Leave this blank and save to disable Google Analytics. Changing it
            affects new page loads without a deployment.
          </p>
          <div className="storage-actions">
            <button className="btn btn-primary" disabled={busy}>
              {busy ? "Saving…" : "Save analytics ID"}
            </button>
            <button
              className="btn btn-outline-light"
              type="button"
              disabled={busy}
              onClick={() => {
                setMeasurementId(savedId);
                setEditing(false);
                setError("");
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <dl className="integration-details">
            <Detail
              label="Measurement ID"
              value={measurementId || "Not configured"}
            />
            <Detail
              label="Coverage"
              value="Public website and authentication pages"
            />
            <Detail
              label="Consent"
              value="Required before Google Analytics loads"
            />
          </dl>
          {canEdit && (
            <div className="integration-actions">
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => setEditing(true)}
              >
                {measurementId ? "Change ID" : "Configure"}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function CredentialIntegrationCard({
  summary,
  canEdit,
  changed,
}: {
  summary: CredentialIntegrationSummary;
  canEdit: boolean;
  changed: (value: CredentialIntegrationSummary) => void;
}) {
  const definition = integrationDefinitions[summary.key];
  const builtIn = ["geocoding", "web_push", "maps"].includes(summary.key) && summary.configured;
  const [editing, setEditing] = useState(false);
  const [provider, setProvider] = useState(
    summary.key === "stripe" ? "stripe" : summary.provider,
  );
  const [configuration, setConfiguration] = useState<Record<string, string>>(
    summary.configuration,
  );
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  useEffect(() => {
    setProvider(summary.key === "stripe" ? "stripe" : summary.provider);
    setConfiguration(summary.configuration);
    setSecrets({});
  }, [summary.updatedAt, summary.key]);
  function cancel() {
    setProvider(summary.key === "stripe" ? "stripe" : summary.provider);
    setConfiguration(summary.configuration);
    setSecrets({});
    setEditing(false);
    setError("");
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const supplied = Object.fromEntries(
        Object.entries(secrets).filter(([, value]) => value.trim()),
      );
      const editableNames = new Set(
        definition.fields
          .filter((field) => !field.secret)
          .map((field) => field.name),
      );
      const editableConfiguration = Object.fromEntries(
        Object.entries(configuration).filter(([name]) =>
          editableNames.has(name),
        ),
      );
      const input: CredentialIntegrationInput = {
        provider,
        configuration: editableConfiguration,
        ...(Object.keys(supplied).length ? { secrets: supplied } : {}),
      };
      const result = await saveCredentialIntegration(summary.key, input);
      changed(result);
      setEditing(false);
      setNotice("Integration settings saved securely.");
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Unable to save integration settings.",
      );
    } finally {
      setBusy(false);
    }
  }
  const ordinary = definition.fields.filter((field) => !field.secret);
  const secret = definition.fields.filter((field) => field.secret);
  return (
    <section className="team-panel integration-card">
      <div className="admin-section-heading">
        <div>
          <p className="admin-kicker">External service</p>
          <h2 className="h5 mb-1">{definition.title}</h2>
          <p className="text-secondary small mb-0">{definition.description}</p>
        </div>
        <span
          className={`status-chip ${summary.configured ? "status-active" : "status-grace"}`}
        >
          {builtIn ? "Built in" : summary.configured ? "Configured" : "Setup required"}
        </span>
      </div>
      {error && <div className="alert alert-danger py-2 mt-3">{error}</div>}
      {notice && <div className="alert alert-success py-2 mt-3">{notice}</div>}
      {editing ? (
        <form className="storage-form mt-3" onSubmit={save}>
          {summary.key !== "stripe" && (
            <Field label="Provider">
              <input
                className="form-control"
                required
                maxLength={80}
                placeholder={definition.providerPlaceholder}
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
              />
            </Field>
          )}
          {ordinary.map((field) => (
            <Field key={field.name} label={field.label}>
              <input
                className="form-control"
                maxLength={2000}
                placeholder={field.placeholder}
                value={configuration[field.name] ?? ""}
                onChange={(e) =>
                  setConfiguration((current) => ({
                    ...current,
                    [field.name]: e.target.value,
                  }))
                }
              />
            </Field>
          ))}
          {secret.map((field) => (
            <Field
              key={field.name}
              label={`${field.label}${summary.configured ? " · leave blank to retain" : ""}`}
            >
              <input
                className="form-control"
                type="password"
                autoComplete="new-password"
                maxLength={4000}
                value={secrets[field.name] ?? ""}
                onChange={(e) =>
                  setSecrets((current) => ({
                    ...current,
                    [field.name]: e.target.value,
                  }))
                }
              />
            </Field>
          ))}
          <div className="storage-actions">
            <button className="btn btn-primary" disabled={busy}>
              {busy ? "Saving…" : "Save changes"}
            </button>
            <button
              className="btn btn-outline-light"
              type="button"
              disabled={busy}
              onClick={cancel}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <dl className="integration-details">
            <Detail
              label="Provider"
              value={summary.provider || "Not configured"}
            />
            {summary.key === "stripe" &&
              summary.configuration.liveCatalogueStatus && (
                <Detail
                  label="Live catalogue"
                  value={summary.configuration.liveCatalogueStatus}
                />
              )}{" "}
            {summary.key === "stripe" &&
              summary.configuration.sandboxCatalogueStatus && (
                <Detail
                  label="Sandbox catalogue"
                  value={summary.configuration.sandboxCatalogueStatus}
                />
              )}{" "}
            {ordinary
              .filter((field) => summary.configuration[field.name])
              .map((field) => (
                <Detail
                  key={field.name}
                  label={field.label}
                  value={summary.configuration[field.name]}
                />
              ))}
            {secret.map((field) => (
              <Detail
                key={field.name}
                label={field.label}
                value={summary.secretHints[field.name] ?? "Not configured"}
              />
            ))}
          </dl>
          {canEdit && !builtIn && (
            <div className="integration-actions">
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => setEditing(true)}
              >
                {summary.configured ? "Edit settings" : "Configure"}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function StoragePanel({ canEdit }: { canEdit: boolean }) {
  const [settings, setSettings] = useState<StorageSettings | null>(null);
  const [form, setForm] = useState<StorageSettingsInput>({
    endpoint: "",
    region: "",
    bucket: "",
  });
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<"save" | "test" | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    void getStorageSettings()
      .then((value) => {
        setSettings(value);
        setForm(fromSettings(value));
      })
      .catch((caught) =>
        setError(
          caught instanceof ApiError
            ? caught.message
            : "Unable to load storage settings.",
        ),
      );
  }, []);
  function updateEndpoint(endpoint: string) {
    setForm({
      ...form,
      endpoint,
      ...(!form.region && regionFrom(endpoint)
        ? { region: regionFrom(endpoint) }
        : {}),
    });
  }
  function cancel() {
    if (settings) setForm(fromSettings(settings));
    setEditing(false);
    setError("");
    setNotice("");
  }
  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy("save");
    setError("");
    setNotice("");
    try {
      const saved = await saveStorageSettings(form);
      setSettings(saved);
      setForm(fromSettings(saved));
      setEditing(false);
      setNotice("Storage settings saved securely.");
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Unable to save storage settings.",
      );
    } finally {
      setBusy(null);
    }
  }
  async function test() {
    setBusy("test");
    setError("");
    setNotice("");
    try {
      await testStorageSettings(
        editing && form.keyId && form.applicationKey ? form : undefined,
      );
      setNotice(
        "Connection successful. LoadSub can access the private bucket.",
      );
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Backblaze connection failed. Check the endpoint, region, bucket and key permissions.",
      );
    } finally {
      setBusy(null);
    }
  }
  return (
    <section className="team-panel integration-card">
      <div className="admin-section-heading">
        <div>
          <p className="admin-kicker">File storage</p>
          <h2 className="h5 mb-1">Backblaze B2</h2>
          <p className="text-secondary small mb-0">
            Private document storage through its S3-compatible API.
          </p>
        </div>
        <span
          className={`status-chip ${settings?.configured ? "status-active" : "status-grace"}`}
        >
          {settings?.configured ? "Configured" : "Setup required"}
        </span>
      </div>
      {error && <div className="alert alert-danger py-2 mt-3">{error}</div>}
      {notice && <div className="alert alert-success py-2 mt-3">{notice}</div>}
      {editing ? (
        <form onSubmit={save} className="storage-form mt-3">
          <Field label="S3 endpoint">
            <input
              className="form-control"
              required
              value={form.endpoint}
              onChange={(e) => updateEndpoint(e.target.value)}
            />
          </Field>
          <Field label="Region">
            <input
              className="form-control"
              required
              value={form.region}
              onChange={(e) => setForm({ ...form, region: e.target.value })}
            />
          </Field>
          <Field label="Bucket">
            <input
              className="form-control"
              required
              value={form.bucket}
              onChange={(e) => setForm({ ...form, bucket: e.target.value })}
            />
          </Field>
          <Field
            label={`Key ID${settings?.configured ? " · leave blank to retain" : ""}`}
          >
            <input
              className="form-control"
              autoComplete="off"
              value={form.keyId ?? ""}
              onChange={(e) =>
                setForm({ ...form, keyId: e.target.value || undefined })
              }
            />
          </Field>
          <Field
            label={`Application key${settings?.configured ? " · leave blank to retain" : ""}`}
          >
            <input
              className="form-control"
              type="password"
              autoComplete="new-password"
              value={form.applicationKey ?? ""}
              onChange={(e) =>
                setForm({
                  ...form,
                  applicationKey: e.target.value || undefined,
                })
              }
            />
          </Field>
          <div className="storage-actions">
            <button className="btn btn-primary" disabled={!!busy}>
              {busy === "save" ? "Saving…" : "Save changes"}
            </button>
            <button
              className="btn btn-outline-light"
              type="button"
              disabled={!!busy}
              onClick={cancel}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <dl className="integration-details">
            <Detail label="S3 endpoint" value={settings?.endpoint} />
            <Detail label="Region" value={settings?.region} />
            <Detail label="Bucket" value={settings?.bucket} />
            <Detail
              label="Key ID"
              value={
                settings?.keyIdHint
                  ? `•••• ${settings.keyIdHint}`
                  : "Not configured"
              }
            />
          </dl>
          <div className="integration-actions">
            {canEdit && (
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => setEditing(true)}
              >
                {settings?.configured ? "Edit settings" : "Configure storage"}
              </button>
            )}
            {canEdit && settings?.configured && (
              <button
                className="btn btn-outline-light"
                type="button"
                disabled={!!busy}
                onClick={() => void test()}
              >
                {busy === "test" ? "Testing…" : "Test connection"}
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function UsersPanel({ role }: { role: string | null }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null),
    [error, setError] = useState("");
  useEffect(() => {
    void getAdminUsers()
      .then(setUsers)
      .catch((e) =>
        setError(e instanceof Error ? e.message : "Unable to load users"),
      );
  }, []);
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!users) return <Loading />;
  return (
    <section className="team-panel">
      <div className="admin-section-heading">
        <div>
          <h2 className="h5">Users and platform roles</h2>
          <p className="text-secondary small">
            The platform owner is fixed and cannot be transferred here. Role
            changes revoke existing sessions.
          </p>
        </div>
      </div>
      <div className="admin-table-wrap">
        <table className="table admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Companies</th>
              <th>Platform role</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>
                  <strong>{user.email}</strong>
                  <small className="d-block text-secondary">
                    {user.verified ? "Verified" : "Unverified"}
                  </small>
                </td>
                <td>{user.companies.map((x) => x.name).join(", ") || "—"}</td>
                <td>
                  {role === "owner" && user.platformRole !== "owner" ? (
                    <select
                      className="form-select form-select-sm"
                      value={user.platformRole ?? ""}
                      onChange={(e) =>
                        void updateAdminUser(user.id, {
                          platformRole: (e.target.value || null) as
                            "admin" | "support" | "finance" | null,
                        }).then(() => getAdminUsers().then(setUsers))
                      }
                    >
                      <option value="">None</option>
                      <option value="admin">Admin</option>
                      <option value="support">Support</option>
                      <option value="finance">Finance</option>
                    </select>
                  ) : (
                    <span className="status-chip">
                      {user.platformRole ?? "ordinary"}
                    </span>
                  )}
                </td>
                <td>
                  {role === "owner" && user.platformRole !== "owner" ? (
                    <button
                      className={`btn btn-sm ${user.active ? "btn-outline-danger" : "btn-outline-light"}`}
                      onClick={() =>
                        void updateAdminUser(user.id, {
                          active: !user.active,
                        }).then(() => getAdminUsers().then(setUsers))
                      }
                    >
                      {user.active ? "Lock user" : "Reactivate"}
                    </button>
                  ) : user.active ? (
                    "Active"
                  ) : (
                    "Locked"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function InspectionPanel({ role }: { role: string | null }) {
  const all: AdminInspectionKind[] = [
      "groups",
      "subscriptions",
      "loads",
      "bids",
      "assignments",
      "pods",
      "invoices",
      "disputes",
      "files",
    ],
    allowed =
      role === "finance"
        ? (["subscriptions", "invoices"] as AdminInspectionKind[])
        : role === "support"
          ? ([
              "groups",
              "loads",
              "bids",
              "assignments",
              "pods",
              "disputes",
            ] as AdminInspectionKind[])
          : all;
  const [kind, setKind] = useState<AdminInspectionKind>(allowed[0]!),
    [data, setData] = useState<AdminInspection | null>(null),
    [error, setError] = useState("");
  useEffect(() => {
    setData(null);
    void getAdminInspection(kind)
      .then(setData)
      .catch((e) =>
        setError(e instanceof Error ? e.message : "Unable to inspect records"),
      );
  }, [kind]);
  const keys = data?.rows[0] ? Object.keys(data.rows[0]) : [];
  return (
    <>
      <section className="team-panel">
        <div className="admin-section-heading">
          <div>
            <h2 className="h5">Record inspection</h2>
            <p className="text-secondary small">
              Role-scoped metadata only. Support views exclude prices, private
              files and credentials.
            </p>
          </div>
          <select
            className="form-select"
            style={{ maxWidth: 260 }}
            value={kind}
            onChange={(e) => setKind(e.target.value as AdminInspectionKind)}
          >
            {allowed.map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </div>
        {error && <div className="alert alert-danger">{error}</div>}
        {!data ? (
          <Loading />
        ) : (
          <div className="admin-table-wrap">
            <table className="table admin-table">
              <thead>
                <tr>
                  {keys.map((k) => (
                    <th key={k}>{k.replaceAll("_", " ")}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => (
                  <tr key={String(row.id ?? i)}>
                    {keys.map((k) => (
                      <td key={k}>{cell(row[k])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <small className="text-secondary">
              {data.total} records · page {data.page} of {data.pages}
            </small>
          </div>
        )}
      </section>
    </>
  );
}


function SeoRankingsPanel() {
  const [range, setRange] = useState<SeoRangeDays>(28);
  const [data, setData] = useState<SeoPerformance | null>(null);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    setData(null);
    setError("");
    void getAdminSeoPerformance(range)
      .then(setData)
      .catch((caught) => setError(caught instanceof Error ? caught.message : "Search performance could not be loaded."));
  }, [range]);
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!data) return <Loading />;
  if (!data.configured) return (
    <section className="team-panel">
      <p className="admin-kicker">Connection required</p>
      <h2 className="h5">Connect Google Search Console</h2>
      <p className="text-secondary mb-2">The rankings dashboard is ready, but the production API does not yet have read-only Search Console credentials.</p>
      <ol className="text-secondary small mb-0">
        <li>Create or choose a Google Cloud service account and enable the Search Console API.</li>
        <li>Add its email as a user on the LoadSub Search Console property.</li>
        <li>Set <code>GOOGLE_SEARCH_CONSOLE_SITE_URL</code> and <code>GOOGLE_SEARCH_CONSOLE_SERVICE_ACCOUNT_JSON</code> on the API service.</li>
      </ol>
    </section>
  );
  const q = filter.trim().toLowerCase();
  const rows = data.rows.filter((row) => !q || row.query.toLowerCase().includes(q) || row.page.toLowerCase().includes(q));
  const change = data.totals.position === null || data.previousTotals.position === null ? null : data.previousTotals.position - data.totals.position;
  return <div className="integration-stack seo-rankings">
    <section className="team-panel">
      <div className="admin-section-heading">
        <div>
          <h2 className="h5 mb-1">Google organic search</h2>
          <p className="text-secondary small mb-0">{data.startDate} to {data.endDate} · final data · {data.property}</p>
        </div>
        <select className="form-select" aria-label="Reporting period" value={range} onChange={(event) => setRange(Number(event.target.value) as SeoRangeDays)} style={{maxWidth: 150}}>
          <option value={7}>Last 7 days</option>
          <option value={28}>Last 28 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>
    </section>
    <section className="admin-stats">
      <Stat label="Clicks" value={data.totals.clicks} />
      <Stat label="Impressions" value={data.totals.impressions} />
      <Stat label="CTR" value={(data.totals.ctr * 100).toFixed(1) + "%"} />
      <Stat label="Average position" value={data.totals.position?.toFixed(1) ?? "—"} />
    </section>
    <section className="team-panel">
      <div className="admin-section-heading">
        <div>
          <h2 className="h5 mb-1">Queries and landing pages</h2>
          <p className="text-secondary small mb-0">Position movement compares with the previous {range} days. Positive values mean ranking improved.{change === null ? "" : ` Overall: ${change >= 0 ? "+" : ""}${change.toFixed(1)}.`}</p>
        </div>
        <input className="form-control" aria-label="Filter keywords or pages" placeholder="Filter keyword or page…" value={filter} onChange={(event) => setFilter(event.target.value)} style={{maxWidth: 300}} />
      </div>
      <div className="admin-table-wrap mt-3">
        <table className="table admin-table mb-0">
          <thead><tr><th>Query</th><th>Landing page</th><th>Position</th><th>Movement</th><th>Impressions</th><th>Clicks</th><th>CTR</th></tr></thead>
          <tbody>{rows.map((row) => <tr key={row.query + "\u0000" + row.page}>
            <td><strong>{row.query}</strong></td>
            <td><a href={row.page} target="_blank" rel="noreferrer">{new URL(row.page).pathname || "/"}</a></td>
            <td>{row.position.toFixed(1)}</td>
            <td>{row.positionChange === null ? "New" : <span className={row.positionChange >= 0 ? "text-success" : "text-warning"}>{row.positionChange >= 0 ? "+" : ""}{row.positionChange.toFixed(1)}</span>}</td>
            <td>{row.impressions}</td><td>{row.clicks}</td><td>{(row.ctr * 100).toFixed(1)}%</td>
          </tr>)}</tbody>
        </table>
      </div>
      {rows.length === 0 && <p className="text-secondary mt-3 mb-0">No Search Console rows match this filter.</p>}
    </section>
  </div>;
}

function ConfigurationPanel({ role }: { role: string | null }) {
  const [catalogue, setCatalogue] = useState<InsuranceCatalogueItem[] | null>(
      null,
    ),
    [settings, setSettings] = useState<Record<string, unknown> | null>(null),
    [notice, setNotice] = useState("");
  const canEdit = role === "owner" || role === "admin";
  useEffect(() => {
    void Promise.all([getInsuranceCatalogue(), getAdminSettings()]).then(
      ([c, s]) => {
        setCatalogue(c);
        setSettings(s);
      },
    );
  }, []);
  if (!catalogue || !settings) return <Loading />;
  const defaults = (settings.notification_defaults ?? {}) as Record<
    string,
    boolean
  >;
  const growthMilestones = (settings.growth_notification_milestones ?? {
    signups: 1,
    groupsCreated: 1,
    referralInvites: 1,
  }) as Record<string, number>;
  const growthLabels: Record<string, string> = {
    signups: "New user signups",
    groupsCreated: "Private groups created",
    referralInvites: "Group/referral invitations created",
  };
  function edit(i: number, patch: Partial<InsuranceCatalogueItem>) {
    setCatalogue(
      (current) =>
        current?.map((x, j) => (j === i ? { ...x, ...patch } : x)) ?? null,
    );
  }
  return (
    <div className="integration-stack">
      <section className="team-panel">
        <h2 className="h5">Insurance catalogue defaults</h2>
        {catalogue.map((x, i) => (
          <div className="insurance-rule-fields" key={x.insuranceType}>
            <label>
              <span>{x.label}</span>
              <select
                className="form-select"
                disabled={!canEdit}
                value={x.defaultLevel}
                onChange={(e) =>
                  edit(i, {
                    defaultLevel: e.target.value as typeof x.defaultLevel,
                  })
                }
              >
                <option value="not_required">Not required</option>
                <option value="advisory">Advisory</option>
                <option value="mandatory">Mandatory</option>
              </select>
            </label>
            <label>
              <span>Warning days</span>
              <input
                className="form-control"
                type="number"
                disabled={!canEdit}
                value={x.defaultWarningDays}
                onChange={(e) =>
                  edit(i, { defaultWarningDays: Number(e.target.value) })
                }
              />
            </label>
            <label>
              <span>Grace days</span>
              <input
                className="form-control"
                type="number"
                disabled={!canEdit}
                value={x.defaultGraceDays}
                onChange={(e) =>
                  edit(i, { defaultGraceDays: Number(e.target.value) })
                }
              />
            </label>
          </div>
        ))}
        {canEdit && (
          <button
            className="btn btn-primary mt-3"
            onClick={() =>
              void saveInsuranceCatalogue(catalogue).then(() =>
                setNotice("Insurance defaults saved and audited."),
              )
            }
          >
            Save insurance defaults
          </button>
        )}
      </section>
      <section className="team-panel">
        <h2 className="h5">Notification defaults</h2>
        <div className="team-actions">
          {["assignments", "messages", "loadUpdates", "compliance"].map(
            (key) => (
              <label className="form-check" key={key}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  disabled={!canEdit}
                  checked={defaults[key] ?? true}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      notification_defaults: {
                        ...defaults,
                        [key]: e.target.checked,
                      },
                    })
                  }
                />
                <span>{key.replace(/([A-Z])/g, " $1")}</span>
              </label>
            ),
          )}
        </div>
        {canEdit && (
          <button
            className="btn btn-primary mt-3"
            onClick={() =>
              void saveAdminSetting(
                "notification_defaults",
                settings.notification_defaults ?? {},
              ).then(() =>
                setNotice("Notification defaults saved and audited."),
              )
            }
          >
            Save notification defaults
          </button>
        )}
      </section>
      {role === "owner" && (
        <section className="team-panel">
          <h2 className="h5">Growth milestone notifications</h2>
          <p className="text-secondary small">
            Notify the platform owner in LoadSub, and by enabled push or email
            channels, whenever each counter reaches the selected interval.
          </p>
          <div className="insurance-rule-fields">
            {Object.entries(growthLabels).map(([key, label]) => (
              <label key={key}>
                <span>{label}</span>
                <select
                  className="form-select"
                  value={growthMilestones[key] ?? 0}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      growth_notification_milestones: {
                        ...growthMilestones,
                        [key]: Number(event.target.value),
                      },
                    })
                  }
                >
                  <option value={0}>Off</option>
                  <option value={1}>Every event</option>
                  <option value={5}>Every 5</option>
                  <option value={10}>Every 10</option>
                  <option value={50}>Every 50</option>
                </select>
              </label>
            ))}
          </div>
          <button
            className="btn btn-primary mt-3"
            onClick={() =>
              void saveAdminSetting(
                "growth_notification_milestones",
                settings.growth_notification_milestones ?? growthMilestones,
              ).then(() =>
                setNotice("Growth milestone notifications saved and audited."),
              )
            }
          >
            Save growth notifications
          </button>
        </section>
      )}
      {role === "owner" && <FileMigrationPanel onComplete={setNotice} />}{" "}
      {notice && <div className="alert alert-success">{notice}</div>}
    </div>
  );
}

function FileMigrationPanel({
  onComplete,
}: {
  onComplete: (value: string) => void;
}) {
  const [provider, setProvider] = useState("s3_replacement"),
    [target, setTarget] = useState<StorageSettingsInput>({
      endpoint: "",
      region: "",
      bucket: "",
      keyId: "",
      applicationKey: "",
    }),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const set = (key: keyof StorageSettingsInput, value: string) =>
    setTarget((current) => ({ ...current, [key]: value }));
  return (
    <section className="team-panel">
      <h2 className="h5">Provider-neutral file migration</h2>
      <p className="text-secondary small">
        Copies each object to the replacement bucket, downloads it again and
        verifies its SHA-256 checksum. The original remains authoritative
        throughout.
      </p>
      <div className="insurance-rule-fields">
        <Field label="Provider identifier">
          <input
            className="form-control"
            value={provider}
            onChange={(e) =>
              setProvider(
                e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "_"),
              )
            }
          />
        </Field>
        <Field label="S3 endpoint">
          <input
            className="form-control"
            value={target.endpoint}
            onChange={(e) => set("endpoint", e.target.value)}
          />
        </Field>
        <Field label="Region">
          <input
            className="form-control"
            value={target.region}
            onChange={(e) => set("region", e.target.value)}
          />
        </Field>
        <Field label="Bucket">
          <input
            className="form-control"
            value={target.bucket}
            onChange={(e) => set("bucket", e.target.value)}
          />
        </Field>
        <Field label="Key ID">
          <input
            className="form-control"
            autoComplete="off"
            value={target.keyId ?? ""}
            onChange={(e) => set("keyId", e.target.value)}
          />
        </Field>
        <Field label="Application key">
          <input
            className="form-control"
            type="password"
            autoComplete="new-password"
            value={target.applicationKey ?? ""}
            onChange={(e) => set("applicationKey", e.target.value)}
          />
        </Field>
      </div>
      {error && <div className="alert alert-danger mt-3">{error}</div>}
      <button
        className="btn btn-outline-light mt-3"
        disabled={
          busy || !provider || Object.values(target).some((value) => !value)
        }
        onClick={() => {
          setBusy(true);
          setError("");
          void queueFileMigration(provider, target)
            .then((result) =>
              onComplete(
                `${result.verified} file objects copied and checksum verified; ${result.queued - result.verified} remain queued.`,
              ),
            )
            .catch((caught) =>
              setError(
                caught instanceof Error
                  ? caught.message
                  : "File migration failed safely; original files remain authoritative.",
              ),
            )
            .finally(() => setBusy(false));
        }}
      >
        {busy ? "Copying and verifying…" : "Start verified migration"}
      </button>
    </section>
  );
}

function HealthPanel() {
  const [data, setData] = useState<AdminHealth | null>(null),
    [deliveries, setDeliveries] = useState<
      Awaited<ReturnType<typeof getFailedNotificationDeliveries>>
    >([]);
  useEffect(() => {
    void Promise.all([
      getAdminHealth().then(setData),
      getFailedNotificationDeliveries().then(setDeliveries),
    ]);
  }, []);
  if (!data) return <Loading />;
  const failedJobs = data.jobs.filter((x) => x.status === "failed"),
    latestByJob = Array.from(
      new Map(data.jobs.map((x) => [x.jobName, x])).values(),
    );
  return (
    <>
      <section className="admin-stats">
        <article className="admin-stat">
          <span>API</span>
          <strong>{data.api.status === "ok" ? "Healthy" : "Problem"}</strong>
          <small>{new Date(data.api.checkedAt).toLocaleString("en-GB")}</small>
        </article>
        <article className="admin-stat">
          <span>Worker</span>
          <strong>{data.worker.status.replace("_", " ")}</strong>
          <small>
            {data.worker.lastRunAt
              ? new Date(data.worker.lastRunAt).toLocaleString("en-GB")
              : "No run recorded"}
          </small>
        </article>
        <Stat label="Failed jobs" value={failedJobs.length} />
        <Stat label="Failed notifications" value={deliveries.length} />
      </section>
      <section className="team-panel mt-3">
        <div className="admin-section-heading">
          <div>
            <h2 className="h5 mb-1">Needs attention</h2>
            <p className="text-secondary small mb-0">
              Only failures are shown here. Successful routine work, including
              insurance-expiry scans, is kept in the collapsed history below.
            </p>
          </div>
        </div>
        {failedJobs.length === 0 && deliveries.length === 0 ? (
          <p className="admin-health-clear mb-0">
            No worker or notification failures need attention.
          </p>
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="table admin-table">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Started</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {failedJobs.map((x) => (
                    <tr key={x.id}>
                      <td>{labelEvent(x.jobName)}</td>
                      <td>{new Date(x.startedAt).toLocaleString("en-GB")}</td>
                      <td>{x.errorMessage ?? "No detail recorded"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {deliveries.length > 0 && (
              <>
                <h3 className="h6 mt-3">Notification delivery failures</h3>
                <div className="admin-table-wrap">
                  <table className="table admin-table">
                    <thead>
                      <tr>
                        <th>Channel</th>
                        <th>Status</th>
                        <th>Attempts</th>
                        <th>Next attempt</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deliveries.map((x) => (
                        <tr key={x.id}>
                          <td>{x.channel.replace("_", " ")}</td>
                          <td>{x.status}</td>
                          <td>{x.attempts}</td>
                          <td>
                            {new Date(x.availableAt).toLocaleString("en-GB")}
                          </td>
                          <td>{x.lastError ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </section>
      <details className="team-panel admin-health-history mt-3">
        <summary>
          <span>
            <strong>Routine worker history</strong>
            <small>
              {latestByJob.length} job type{latestByJob.length === 1 ? "" : "s"}{" "}
              · {data.jobs.length} recent run{data.jobs.length === 1 ? "" : "s"}
            </small>
          </span>
        </summary>
        <div className="admin-table-wrap mt-3">
          <table className="table admin-table mb-0">
            <thead>
              <tr>
                <th>Job</th>
                <th>Latest status</th>
                <th>Processed</th>
                <th>Latest run</th>
              </tr>
            </thead>
            <tbody>
              {latestByJob.map((x) => (
                <tr key={x.jobName}>
                  <td>{labelEvent(x.jobName)}</td>
                  <td>{x.status}</td>
                  <td>{x.processedCount}</td>
                  <td>{new Date(x.startedAt).toLocaleString("en-GB")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </>
  );
}

function ErrorsPanel() {
  const [result, setResult] = useState<AuditResult | null>(null),
    [page, setPage] = useState(1),
    [error, setError] = useState("");
  useEffect(() => {
    setError("");
    void getAdminAudit({ eventType: "system.api_error", page })
      .then(setResult)
      .catch((caught) =>
        setError(
          caught instanceof ApiError
            ? caught.message
            : "Unable to load captured errors.",
        ),
      );
  }, [page]);
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!result) return <Loading />;
  return (
    <section className="team-panel">
      <div className="admin-section-heading">
        <div>
          <h2 className="h5 mb-1">Captured application errors</h2>
          <p className="text-secondary small mb-0">
            {result.total.toLocaleString("en-GB")} unexpected failure
            {result.total === 1 ? "" : "s"} · newest first. Customer-facing
            messages contain none of this technical detail.
          </p>
        </div>
        <NavLink className="btn btn-sm btn-outline-light" to="/admin/audit">
          Open full audit
        </NavLink>
      </div>
      {result.events.length === 0 ? (
        <p className="admin-health-clear mb-0">
          No unexpected application errors have been captured.
        </p>
      ) : (
        <div className="admin-error-list">
          {result.events.map((event, index) => (
            <details
              className="admin-error-event"
              key={event.id}
              open={index === 0 && page === 1}
            >
              <summary>
                <span>
                  <strong>
                    {String(event.metadata.method ?? "Request")}{" "}
                    {String(
                      event.metadata.route ??
                        event.metadata.url ??
                        "Unknown route",
                    )}
                  </strong>
                  <small>
                    {event.companyName ?? "Platform"} ·{" "}
                    {event.actorEmail ?? "System"}
                  </small>
                </span>
                <time>
                  {new Date(event.occurredAt).toLocaleString("en-GB")}
                </time>
              </summary>
              <dl>
                <Detail
                  label="Company"
                  value={event.companyName ?? event.companyId ?? "Platform"}
                />
                <Detail
                  label="User"
                  value={event.actorEmail ?? event.actorUserId ?? "System"}
                />
                <Detail
                  label="Request ID"
                  value={event.requestId ?? "Not recorded"}
                />
                <Detail
                  label="Error"
                  value={String(
                    event.metadata.message ??
                      event.metadata.errorName ??
                      "No message recorded",
                  )}
                />
              </dl>
              <pre>{JSON.stringify(event.metadata, null, 2)}</pre>
            </details>
          ))}
        </div>
      )}
      {result.pages > 1 && (
        <div className="audit-pagination">
          <button
            className="btn btn-sm btn-outline-light"
            disabled={page <= 1}
            onClick={() => setPage((value) => value - 1)}
          >
            Previous
          </button>
          <span>
            {page} / {result.pages}
          </span>
          <button
            className="btn btn-sm btn-outline-light"
            disabled={page >= result.pages}
            onClick={() => setPage((value) => value + 1)}
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
}

function cell(value: unknown) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  const text = String(value);
  return text.length > 80 ? text.slice(0, 77) + "…" : text;
}
function Loading() {
  return (
    <section className="team-panel">
      <p className="text-secondary mb-0">Loading platform data…</p>
    </section>
  );
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label>
      <span>{label}</span>
      {children}
    </label>
  );
}
function Detail({
  label,
  value,
}: {
  label: string;
  value: string | undefined;
}) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value || "Not configured"}</dd>
    </div>
  );
}
function fromSettings(value: StorageSettings): StorageSettingsInput {
  return {
    endpoint: value.endpoint,
    region: value.region,
    bucket: value.bucket,
  };
}
function regionFrom(endpoint: string) {
  const match = /(?:https?:\/\/)?s3\.([a-z0-9-]+)\.backblazeb2\.com/i.exec(
    endpoint.trim(),
  );
  return match?.[1] ?? "";
}
function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <article className="admin-stat">
      <span>{label}</span>
      <strong>{typeof value === "number" ? value.toLocaleString("en-GB") : value}</strong>
    </article>
  );
}
function formatDate(value: string) {
  return new Date(value).toLocaleDateString("en-GB");
}
